import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCircle, Warning, Info, WifiHigh } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useNotificationSocket } from '../hooks/useNotificationSocket';
import { BACKEND_URL } from '../config';

const NotificationCenter = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(data.count);
    } catch (err) { console.error(err.message);
      // silent
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notifications?limit=30`, { withCredentials: true });
      setNotifications(data.notifications || []);
    } catch (err) { console.error(err.message);
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  // WebSocket handler for real-time updates
  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'unread_count') {
      setUnreadCount(msg.count);
    } else if (msg.type === 'new_notification') {
      setUnreadCount(msg.unread_count);
      setNotifications(prev => [msg.notification, ...prev].slice(0, 30));
    }
  }, []);

  const { connected } = useNotificationSocket(handleWsMessage);

  // Fallback polling if WebSocket is not connected
  useEffect(() => {
    fetchUnreadCount();
    if (!connected) {
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchUnreadCount, connected]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (id) => {
    await axios.patch(`${BACKEND_URL}/api/notifications/${id}/read`, {}, { withCredentials: true });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await axios.post(`${BACKEND_URL}/api/notifications/mark-all-read`, {}, { withCredentials: true });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const getIcon = (type) => {
    if (type === 'dip_alert') return <Warning size={16} weight="fill" className="text-destructive shrink-0" />;
    if (type === 'goal') return <CheckCircle size={16} weight="fill" className="text-accent shrink-0" />;
    return <Info size={16} weight="fill" className="text-muted-foreground shrink-0" />;
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        data-testid="notification-bell"
        variant="outline"
        size="sm"
        className="rounded-sm relative"
        onClick={() => setOpen(!open)}
        title={connected ? "Notifications (live)" : "Notifications"}
      >
        <Bell size={16} weight={unreadCount > 0 ? 'fill' : 'regular'} />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {connected && <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-accent" title="Live connection" />}
      </Button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border border-border rounded-sm shadow-lg z-50 max-h-[480px] flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold tracking-tight flex items-center gap-2">
              Notifications
              {connected && <WifiHigh size={12} weight="fill" className="text-accent" title="Live" />}
            </span>
            {unreadCount > 0 && (
              <button
                data-testid="mark-all-read-btn"
                onClick={markAllRead}
                className="text-xs text-accent hover:underline flex items-center gap-1"
              >
                <Check size={12} weight="bold" /> Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No notifications yet</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-border cursor-pointer transition-colors hover:bg-muted/50 ${!n.read ? 'bg-accent/5' : ''}`}
                  onClick={() => !n.read && markAsRead(n.id)}
                >
                  <div className="mt-0.5">{getIcon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-accent"></span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
